import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminSidebar } from '../../components/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DollarSign } from 'lucide-react';
import { SmsSettingsCard } from '../../components/admin/SmsSettingsCard';

import { API_URL } from '../../utils/api';
import { getErrorMessage } from '../../utils/errors';

export default function AdminPricing() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/pricing`);
      setPricing(response.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/pricing`, {
        digital_base_price: pricing.digital_base_price,
        print_base_price: pricing.print_base_price,
        print_per_card_price: pricing.print_per_card_price
      });
      toast.success('Pricing updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update pricing'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper">
        <AdminSidebar />
        <div className="ml-64 p-8 flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <AdminSidebar />
      <div className="ml-64 p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif text-3xl font-bold text-ink mb-2">Pricing</h1>
          <p className="text-ink/60 mb-8">Configure default pricing for invitations</p>
          
          <div className="max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-gold" />
                  Default Pricing Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="digital_price">Digital Base Price ($)</Label>
                  <Input
                    id="digital_price"
                    type="number"
                    step="0.01"
                    value={pricing?.digital_base_price || 0}
                    onChange={(e) => setPricing({ ...pricing, digital_base_price: parseFloat(e.target.value) })}
                    className="mt-2"
                    data-testid="digital-base-price"
                  />
                  <p className="text-sm text-ink/50 mt-1">Base price for digital invitations</p>
                </div>
                
                <div>
                  <Label htmlFor="print_price">Print Base Price ($)</Label>
                  <Input
                    id="print_price"
                    type="number"
                    step="0.01"
                    value={pricing?.print_base_price || 0}
                    onChange={(e) => setPricing({ ...pricing, print_base_price: parseFloat(e.target.value) })}
                    className="mt-2"
                    data-testid="print-base-price"
                  />
                  <p className="text-sm text-ink/50 mt-1">Base price for printed invitations</p>
                </div>
                
                <div>
                  <Label htmlFor="per_card_price">Price Per Card ($)</Label>
                  <Input
                    id="per_card_price"
                    type="number"
                    step="0.01"
                    value={pricing?.print_per_card_price || 0}
                    onChange={(e) => setPricing({ ...pricing, print_per_card_price: parseFloat(e.target.value) })}
                    className="mt-2"
                    data-testid="per-card-price"
                  />
                  <p className="text-sm text-ink/50 mt-1">Additional cost per printed card</p>
                </div>
                
                <Button onClick={handleSave} disabled={saving} className="w-full bg-ink text-paper hover:bg-ink/90" data-testid="save-pricing-btn">
                  {saving ? 'Saving...' : 'Save Pricing'}
                </Button>
              </CardContent>
            </Card>
            <SmsSettingsCard />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
